// ===========================================================================
// Cargo Market · Módulo A — booking engine (end-to-end transacción + pago)
// ---------------------------------------------------------------------------
// Orchestrates the purchase of cargo space the épica asks for:
//   1. Flujo de booking contra la API del proveedor (reservar el espacio).
//   2. Pago de la transacción.
//   3. Generación de confirmación/documentos (booking ref, AWB/BL).
//   4. Manejo de fallos (disponibilidad caducada, pago rechazado, rollback).
//
// The provider booking and the charge are *simulated* client-side adapters that
// stand in for the real provider/payment APIs — same seams a backend would own.
// They are deliberately isolated behind `providerAdapter` and `paymentGateway`
// so wiring the real integrations later is a drop-in replacement. `runBooking`
// drives the state machine and, crucially, rolls back a partial transaction
// (releases the provider hold / refunds the charge) when a later step fails.
// ===========================================================================

import type {
  BookingDocument,
  BookingEvent,
  BookingQuote,
  BookingStatus,
  PaymentStatus,
} from "./bookingTypes";

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const rand = (n: number) => Math.floor(Math.random() * n);
const pad = (n: number, len: number) => String(n).padStart(len, "0");

// ---------------------------------------------------------------------------
// Provider adapter — stands in for the carrier/forwarder booking API.
// ---------------------------------------------------------------------------

/**
 * Whether the provider supports end-to-end booking from the app. Mirrors the
 * épica's "donde el proveedor lo soporte": air/ocean carriers that exposed a
 * booking API are bookable; some sea LCL consolidators are quote-only. When a
 * provider is unknown we optimistically allow booking (demo data).
 */
const QUOTE_ONLY_PROVIDERS = new Set(["EuroFreight"]);

export function providerSupportsBooking(provider: string | undefined): boolean {
  if (!provider) return true;
  return !QUOTE_ONLY_PROVIDERS.has(provider);
}

export interface AvailabilityHold {
  ref: string;
  /** epoch ms after which the held space is released by the provider. */
  expiresAt: number;
}

export const providerAdapter = {
  /**
   * Confirm the quoted space is still sellable and place a short-lived hold.
   * Returns a hold token + expiry; the rest of the flow must complete before it
   * lapses or the booking fails with "disponibilidad caducada".
   */
  async reserveSpace(quote: BookingQuote): Promise<AvailabilityHold> {
    await wait(900);
    // ~1 in 12 quotes have lost their space between quoting and booking.
    if (rand(12) === 0) {
      throw new BookingError(
        "availability_expired",
        "El proveedor ya no tiene espacio disponible para esta cotización.",
      );
    }
    const ref = `${quote.mode.slice(0, 3).toUpperCase()}-${Date.now()
      .toString(36)
      .toUpperCase()}-${pad(rand(1000), 3)}`;
    return { ref, expiresAt: Date.now() + 15 * 60_000 };
  },

  /** Release a hold (rollback when a later step fails). Never throws. */
  async releaseSpace(hold: AvailabilityHold | null): Promise<void> {
    if (!hold) return;
    await wait(300);
  },

  /** Finalize the reservation once paid → issues the provider booking ref. */
  async confirmBooking(hold: AvailabilityHold): Promise<string> {
    await wait(700);
    if (Date.now() > hold.expiresAt) {
      throw new BookingError(
        "availability_expired",
        "La reserva del proveedor caducó antes de confirmarse.",
      );
    }
    return hold.ref;
  },
};

// ---------------------------------------------------------------------------
// Payment gateway — stands in for the existing cobro infra (Stripe-style).
// ---------------------------------------------------------------------------

export interface PaymentDetails {
  /** "card" | "wallet" | "credit" */
  method: string;
  /** Card number / token; demo cards drive the simulated outcome. */
  cardNumber: string;
  holder: string;
}

export interface ChargeResult {
  ref: string;
  status: PaymentStatus;
}

export const paymentGateway = {
  /**
   * Charge the transaction. Demo rule: a card ending in 0000 is declined, so
   * the rejected-payment + rollback path is testable on demand; everything
   * else authorizes.
   */
  async charge(amount: number, details: PaymentDetails): Promise<ChargeResult> {
    await wait(1100);
    const digits = details.cardNumber.replace(/\D/g, "");
    if (amount <= 0) {
      throw new BookingError("payment_declined", "El monto a cobrar no es válido.");
    }
    if (digits.endsWith("0000")) {
      throw new BookingError(
        "payment_declined",
        "El pago fue rechazado por el emisor de la tarjeta.",
      );
    }
    return {
      ref: `PAY-${Date.now().toString(36).toUpperCase()}-${pad(rand(10000), 4)}`,
      status: "authorized",
    };
  },

  /** Refund an authorized charge (rollback). Never throws. */
  async refund(chargeRef: string | null): Promise<void> {
    if (!chargeRef) return;
    await wait(400);
  },
};

// ---------------------------------------------------------------------------
// Document generation — booking ref, AWB/BL, invoice.
// ---------------------------------------------------------------------------

/** Issue the transport documents for a confirmed booking. */
export function issueDocuments(
  quote: BookingQuote,
  bookingRef: string,
  paymentRef: string,
): { documents: BookingDocument[]; awb: string | null; bl: string | null } {
  const documents: BookingDocument[] = [
    { type: "booking", label: "Confirmación de reserva", reference: bookingRef },
  ];

  let awb: string | null = null;
  let bl: string | null = null;

  const mode = quote.mode.toLowerCase();
  if (mode.includes("aér") || mode.includes("aer")) {
    // Air Waybill: 3-digit airline prefix + 8-digit serial.
    awb = `${pad(rand(1000), 3)}-${pad(rand(100000000), 8)}`;
    documents.push({ type: "awb", label: "Guía aérea (AWB)", reference: awb });
  } else if (mode.includes("marít") || mode.includes("marit")) {
    bl = `BL${Date.now().toString(36).toUpperCase()}${pad(rand(1000), 3)}`;
    documents.push({ type: "bl", label: "Conocimiento de embarque (BL)", reference: bl });
  }

  documents.push({ type: "invoice", label: "Factura de la transacción", reference: paymentRef });
  return { documents, awb, bl };
}

/** Short human folio for the booking list (BKG-XXXXXX). */
export function makeFolio(): string {
  return `BKG-${Date.now().toString(36).toUpperCase().slice(-6)}`;
}

// ---------------------------------------------------------------------------
// Errors + orchestration
// ---------------------------------------------------------------------------

export type BookingFailureCode =
  | "availability_expired"
  | "payment_declined"
  | "provider_unsupported"
  | "unknown";

export class BookingError extends Error {
  code: BookingFailureCode;
  constructor(code: BookingFailureCode, message: string) {
    super(message);
    this.name = "BookingError";
    this.code = code;
  }
}

export interface BookingOutcome {
  status: BookingStatus;
  paymentStatus: PaymentStatus;
  bookingRef: string | null;
  paymentRef: string | null;
  documents: BookingDocument[];
  awb: string | null;
  bl: string | null;
  events: BookingEvent[];
  error?: { code: BookingFailureCode; message: string };
}

/** Emitted to the UI as the transaction advances, for the step indicator. */
export type ProgressFn = (status: BookingStatus, message: string) => void;

/**
 * Run the full transaction. Walks reserving → reserved → payment_pending →
 * paid → confirmed. On any failure it undoes whatever already succeeded
 * (release the hold, refund the charge) and resolves with a `failed`/`cancelled`
 * outcome — it does NOT throw, so the caller always gets a recordable result.
 */
export async function runBooking(
  quote: BookingQuote,
  payment: PaymentDetails,
  onProgress?: ProgressFn,
): Promise<BookingOutcome> {
  const events: BookingEvent[] = [];
  const log = (status: BookingStatus, message: string) => {
    events.push({ status, message, at: Date.now() });
    onProgress?.(status, message);
  };

  // Guard: the épica only allows the purchase "donde el proveedor lo soporte".
  if (!providerSupportsBooking(quote.provider)) {
    log("failed", `El proveedor ${quote.provider} no permite compra en línea (solo cotización).`);
    return {
      status: "failed",
      paymentStatus: "pending",
      bookingRef: null,
      paymentRef: null,
      documents: [],
      awb: null,
      bl: null,
      events,
      error: {
        code: "provider_unsupported",
        message: "Este proveedor no soporta compra end-to-end; usa la cotización.",
      },
    };
  }

  let hold: AvailabilityHold | null = null;
  let paymentRef: string | null = null;

  try {
    // 1) Reserve the space against the provider.
    log("reserving", "Reservando espacio con el proveedor…");
    hold = await providerAdapter.reserveSpace(quote);
    log("reserved", `Espacio reservado (${hold.ref}). Procesando pago…`);

    // 2) Charge the transaction.
    log("payment_pending", "Autorizando el pago…");
    const charge = await paymentGateway.charge(quote.amount, payment);
    paymentRef = charge.ref;
    log("paid", "Pago autorizado. Confirmando reserva…");

    // 3) Confirm the booking and issue documents.
    const bookingRef = await providerAdapter.confirmBooking(hold);
    const { documents, awb, bl } = issueDocuments(quote, bookingRef, paymentRef);
    log("confirmed", `Reserva confirmada. Documentos emitidos (${bookingRef}).`);

    return {
      status: "confirmed",
      paymentStatus: "paid",
      bookingRef,
      paymentRef,
      documents,
      awb,
      bl,
      events,
    };
  } catch (err) {
    const be =
      err instanceof BookingError
        ? err
        : new BookingError("unknown", "Ocurrió un error inesperado en la transacción.");
    log("failed", be.message);

    // Rollback: undo any partial reservation / charge so nothing is left hanging.
    await paymentGateway.refund(paymentRef);
    await providerAdapter.releaseSpace(hold);
    log("cancelled", "Transacción revertida (hold liberado / pago reembolsado).");

    return {
      status: "cancelled",
      paymentStatus: paymentRef ? "refunded" : "declined",
      bookingRef: null,
      paymentRef,
      documents: [],
      awb: null,
      bl: null,
      events,
      error: { code: be.code, message: be.message },
    };
  }
}
